/**
 * Consent Guard — Checks if the user has accepted the current data treatment policy.
 *
 * If consent is missing or outdated, shows a blocking modal that cannot be dismissed
 * without acceptance. Checks local storage first (fast path), then validates via
 * the user's metadata from the API if needed.
 *
 * @see Ley 1581 de 2012 (Habeas Data)
 * @see .kiro/specs/habeas-data-compliance/requirements.md Req 4
 */
import { inject } from '@angular/core';
import { type CanActivateFn } from '@angular/router';
import { ModalController } from '@ionic/angular/standalone';
import { CURRENT_POLICY_VERSION } from '@fsm/shared';
import { ConsentModalComponent } from '../../shared/components/consent-modal/consent-modal.component';
import { ApiService } from '../services/api.service';
import { PersistentStorageService } from '../services/persistent-storage.service';
import { firstValueFrom } from 'rxjs';

interface ConsentResponse {
  data: {
    consentPolicyVersion: string | null;
    consentAcceptedAt: string | null;
  };
  requestId: string;
}

export const consentGuard: CanActivateFn = async () => {
  const modalCtrl = inject(ModalController);
  const api = inject(ApiService);
  const persistent = inject(PersistentStorageService);

  // Fast path: check durable storage (Preferences on native, non-purgeable)
  const localVersion = persistent.getSync('fsm_consent_version');
  if (localVersion === CURRENT_POLICY_VERSION) {
    return true;
  }

  // Fallback: check backend (consent persists across sessions even if localStorage is cleared)
  try {
    const res = await firstValueFrom(api.get<ConsentResponse>('/v1/team/me/consent'));
    if (res.data.consentPolicyVersion === CURRENT_POLICY_VERSION) {
      // Re-cache durably to avoid future API calls
      void persistent.set('fsm_consent_version', CURRENT_POLICY_VERSION);
      return true;
    }
  } catch {
    // API unreachable (offline) — fall through to show modal
  }

  // Show blocking consent modal
  const modal = await modalCtrl.create({
    component: ConsentModalComponent,
    backdropDismiss: false,
    keyboardClose: false,
  });

  await modal.present();
  const { data } = await modal.onWillDismiss();

  // Only allow navigation if user accepted
  return data?.accepted === true;
};
